import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  TITLE_TEMPLATE,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  absoluteUrl,
} from "../../seo/seo.config";

/**
 * Every head tag this component owns carries this attribute, including the
 * defaults hard-coded in public/index.html. Those defaults exist so crawlers
 * and social scrapers that don't execute JavaScript still see sane metadata;
 * on mount we replace them with the route-specific set.
 */
const MANAGED_ATTR = "data-seo";

const clearManagedTags = () => {
  document
    .querySelectorAll(`head [${MANAGED_ATTR}]`)
    .forEach((el) => el.parentNode.removeChild(el));
};

const appendTag = (tagName, attrs, text) => {
  const el = document.createElement(tagName);
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      el.setAttribute(key, String(value));
    }
  });
  el.setAttribute(MANAGED_ATTR, "");
  if (text) el.textContent = text;
  document.head.appendChild(el);
};

const meta = (name, content) => appendTag("meta", { name, content });
const property = (prop, content) =>
  appendTag("meta", { property: prop, content });

/**
 * Route-level SEO. Renders nothing — it writes directly to <head>.
 *
 * Mount exactly one <Seo /> per route: each instance clears the previously
 * managed tags before writing its own, so two mounted at once would fight.
 * Static routes get theirs from the route element in App.js; data-driven
 * routes (salon detail) render their own once the API responds.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  /** Canonical path. Defaults to the current location's pathname. */
  canonical,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  /** og:type — "website" for listings, "article" for policy pages. */
  type = "website",
  noindex = false,
  /** A structured-data object, or an array of them. */
  jsonLd,
}) {
  const location = useLocation();
  const canonicalPath = canonical || location.pathname;

  // Serialising the props keeps the effect from re-running on every render
  // when a parent passes freshly built objects.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const fullTitle = TITLE_TEMPLATE(title);
    const canonicalUrl = absoluteUrl(canonicalPath);
    const imageUrl = absoluteUrl(image);

    document.title = fullTitle;
    clearManagedTags();

    // ── Core ──
    meta("description", description);
    if (keywords) meta("keywords", keywords);
    meta(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    appendTag("link", { rel: "canonical", href: canonicalUrl });

    // ── Open Graph (Facebook, WhatsApp, LinkedIn) ──
    property("og:site_name", SITE_NAME);
    property("og:type", type);
    property("og:title", fullTitle);
    property("og:description", description);
    property("og:url", canonicalUrl);
    property("og:image", imageUrl);
    property("og:image:alt", imageAlt || fullTitle);
    property("og:locale", "en_IN");

    // ── Twitter / X ──
    meta("twitter:card", "summary_large_image");
    meta("twitter:site", TWITTER_HANDLE);
    meta("twitter:title", fullTitle);
    meta("twitter:description", description);
    meta("twitter:image", imageUrl);
    meta("twitter:image:alt", imageAlt || fullTitle);

    // ── Structured data ──
    if (jsonLdKey) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.filter(Boolean).forEach((block) => {
        appendTag("script", { type: "application/ld+json" }, JSON.stringify(block));
      });
    }
  }, [
    title,
    description,
    keywords,
    canonicalPath,
    image,
    imageAlt,
    type,
    noindex,
    jsonLdKey,
    jsonLd,
  ]);

  return null;
}
